import { ChangeEvent, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import cn from "classnames";

import { ReactComponent as PlusSVG } from "@/assets/icons/feather-icons/plus-16-white.svg";

import { ClientStatus, IClient } from "@/modules/client/clients.interface";

import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import TableHeader from "@/shared/components/TableHeader/TableHeader";
import { sortElements } from "@/shared/utils/array/sort.utils";
import { isSubstring } from "@/shared/utils/comparison/comparison";

import { Button, ButtonColorType, ButtonSize } from "@/ui-components/commonComponents/Button/Button";

import { CLIENT_ROUTE, SETTINGS_ROUTE } from "@/routing/AppRouter/routes.constants";

import { ReactComponent as Tool } from "@/assets/icons/feather-icons/tool.svg";
import { ReactComponent as LogIn } from "@/assets/icons/feather-icons/log-in.svg";

import style from "./style.module.css";
import { startCase } from "lodash";

export default function TableClients({
  isAdmin,
  dataList,
  onAddClient,
}: {
  isAdmin: boolean;
  dataList: IClient[];
  onAddClient: () => void;
}) {
  const navigate = useNavigate();
  const ability = useContext(ClientAbilityContext);
  const canReadClientStatus = ability.can(Action.Read, Subject.CLIENT, "status");

  const DEFAULT_PAGE_SIZE = isAdmin ? 8 : 10;
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [tableData, setTableData] = useState<IClient[]>(dataList);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTableData(dataList.filter((item: IClient) => isSubstring(item.name, event.target.value)));
  };

  function generateTableColumns(): ColumnsType<IClient> {
    const clientsTableColumns: ColumnsType<IClient> = [
      {
        key: "name",
        title: "Name",
        dataIndex: "name",
        className: style.tableColumnName,
        ellipsis: true,
        defaultSortOrder: "ascend",
        sorter: (a, b) => sortElements(a.name, b.name),
        render: (value, record) => (
          <Tooltip title={record.name} placement={"topLeft"} overlayStyle={{ maxWidth: "60%", minWidth: "50px" }}>
            <Link to={`/${CLIENT_ROUTE}/${record.clientId}`} className={style.tableLink}>
              {value}
            </Link>
          </Tooltip>
        ),
      },
    ];

    if (canReadClientStatus) {
      clientsTableColumns.push({
        key: "status",
        title: "Status",
        dataIndex: "status",
        className: style.tableColumn,
        defaultFilteredValue: [ClientStatus.Active],
        filters: [
          { text: startCase(ClientStatus.Active), value: ClientStatus.Active },
          { text: startCase(ClientStatus.Inactive), value: ClientStatus.Inactive },
        ],
        onFilter: (value, record) => record.status === value,
        sorter: (a, b) => sortElements(a.status, b.status),
        render: (value) => startCase(value),
      });
    }

    clientsTableColumns.push({
      key: "actions",
      title: "",
      dataIndex: "actions",
      className: cn(style.tableColumn, style.actionsColumnWidth),
      render: (_value, record) => {
        return (
          <div className={style.actionsContainer}>
            {isAdmin && (
              <Button
                onClick={() => {
                  navigate(`/${CLIENT_ROUTE}/${record.clientId}/${SETTINGS_ROUTE}`);
                }}
                colorType={ButtonColorType.transparentDark}
                buttonSize={ButtonSize.small}
                icon={<Tool />}
              >
                Settings
              </Button>
            )}
            <Button
              disabled={record.selected}
              onClick={() => {
                navigate(`/${CLIENT_ROUTE}/${record.clientId}`);
              }}
              colorType={ButtonColorType.dark}
              buttonSize={ButtonSize.small}
              icon={<LogIn />}
            >
              Switch to
            </Button>
          </div>
        );
      },
    });

    return clientsTableColumns;
  }

  return (
    <div className={style.tableBlock}>
      {isAdmin ? (
        <TableHeader tableName="Clients" handleSearchChange={handleSearchChange}>
          <Button onClick={onAddClient} buttonSize={ButtonSize.small} icon={<PlusSVG aria-hidden="true" />}>
            Add Client
          </Button>
        </TableHeader>
      ) : (
        <TableHeader tableName="Clients" handleSearchChange={handleSearchChange} />
      )}
      <Table
        columns={generateTableColumns()}
        dataSource={tableData}
        scroll={{ x: "1000px" }}
        style={{ width: "100%" }}
        pagination={{
          position: ["bottomLeft"],
          pageSize: pageSize,
          onShowSizeChange: (_current, size) => setPageSize(size),
        }}
      />
    </div>
  );
}
